import { type RefObject, useEffect } from 'react';

/** How far the mouse may move before a press on a card becomes a drag rather than a click. */
export const DRAG_THRESHOLD_PX = 5;
/** Snapping waits this long after the last wheel event, so it does not fight each tick. */
const WHEEL_SETTLE_MS = 150;
const LINE_HEIGHT_PX = 16;
const DOM_DELTA_LINE = 1;
const DOM_DELTA_PAGE = 2;

/**
 * Mouse drag-to-scroll and vertical wheel on the strip, bound on the scrolling element
 * itself. Touch and trackpads scroll it natively.
 *
 * While either gesture runs the strip carries `data-gesture`, which suspends scroll snapping
 * in the stylesheet: snapping after every programmatic step pulls the strip back to the card
 * it just left. A drag that moved past the threshold swallows the click it ends with, so it
 * never also selects the card it started on.
 */
export function useRailGestures(trackRef: RefObject<HTMLElement | null>): void {
    useEffect(() => {
        const track = trackRef.current;
        if (!track) {
            return;
        }

        let drag: { startX: number; startScroll: number; moved: boolean } | null = null;
        let swallowNextClick = false;
        let wheelTimer: ReturnType<typeof setTimeout> | undefined;

        const endGesture = () => {
            delete track.dataset.gesture;
        };

        const onPointerMove = (event: PointerEvent) => {
            if (!drag) {
                return;
            }
            const dx = event.clientX - drag.startX;
            if (!drag.moved && Math.abs(dx) <= DRAG_THRESHOLD_PX) {
                return;
            }
            drag.moved = true;
            track.dataset.gesture = 'drag';
            track.scrollLeft = drag.startScroll - dx;
        };
        const onPointerUp = () => {
            if (drag?.moved) {
                swallowNextClick = true;
                endGesture();
            }
            drag = null;
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('pointercancel', onPointerUp);
        };
        const onPointerDown = (event: PointerEvent) => {
            if (event.pointerType !== 'mouse' || event.button !== 0) {
                return;
            }
            // A click that never came, e.g. a drag released outside the strip, must not
            // swallow the next real one.
            swallowNextClick = false;
            drag = { startX: event.clientX, startScroll: track.scrollLeft, moved: false };
            window.addEventListener('pointermove', onPointerMove);
            window.addEventListener('pointerup', onPointerUp);
            window.addEventListener('pointercancel', onPointerUp);
        };
        const onClickCapture = (event: MouseEvent) => {
            if (swallowNextClick) {
                swallowNextClick = false;
                event.preventDefault();
                event.stopPropagation();
            }
        };
        // Thumbnails are images, and dragging an image starts the browser's drag and drop.
        const onDragStart = (event: DragEvent) => event.preventDefault();

        const onWheel = (event: WheelEvent) => {
            if (event.ctrlKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
                return;
            }
            const pixels =
                event.deltaMode === DOM_DELTA_LINE
                    ? event.deltaY * LINE_HEIGHT_PX
                    : event.deltaMode === DOM_DELTA_PAGE
                      ? event.deltaY * track.clientWidth
                      : event.deltaY;
            const range = track.scrollWidth - track.clientWidth;
            const canMove = pixels < 0 ? track.scrollLeft > 0 : track.scrollLeft < range - 1;
            // At either end the page scrolls on, as it would past any other element.
            if (!canMove) {
                return;
            }
            event.preventDefault();
            track.dataset.gesture = 'wheel';
            track.scrollBy({ left: pixels, behavior: 'auto' });
            clearTimeout(wheelTimer);
            wheelTimer = setTimeout(endGesture, WHEEL_SETTLE_MS);
        };

        track.addEventListener('pointerdown', onPointerDown);
        track.addEventListener('click', onClickCapture, true);
        track.addEventListener('dragstart', onDragStart);
        track.addEventListener('wheel', onWheel, { passive: false });

        return () => {
            clearTimeout(wheelTimer);
            onPointerUp();
            track.removeEventListener('pointerdown', onPointerDown);
            track.removeEventListener('click', onClickCapture, true);
            track.removeEventListener('dragstart', onDragStart);
            track.removeEventListener('wheel', onWheel);
        };
    }, [trackRef]);
}
